import { put } from 'redux-saga/effects'
import { displayErrorModal } from 'actions/application'
import { NetworkError } from 'utils/request'
import strings from 'utils/strings'
import { HANDLED, SKIP } from './constants'

/**
 * Domain handler — supporting data load failure (parameter groups / addresses).
 *
 * Shows an error modal when the background lookup data required for editing
 * Reference Parameters or Support Parameters cannot be loaded.
 *
 * This handler only fires for APPLICATION-LEVEL errors. Network-level errors
 * (fetch failures, timeouts, server 5xx) return SKIP so the system handlers
 * and defaultErrorHandler can surface the correct UI (e.g. the timeout modal
 * or a generic failure modal) rather than the domain-specific "supporting data
 * load failure" copy, which would be misleading for infrastructure failures.
 *
 * Usage:
 *   import supportingDataErrorHandler from 'sagas/errorHandlers/supportingDataErrorHandler'
 *
 *   apiAction({
 *     ...,
 *     errorHandlers: [supportingDataErrorHandler],
 *   })
 *
 * @param {Error} err - The caught error with `.code` and `.description`.
 * @returns {'handled'|'skip'}
 */
export default function* supportingDataErrorHandler(err) {
  // Skip network-level errors (NetworkError covers 5xx + NetworkTimeoutError;
  // TypeError covers CORS/offline fetch failures) — same guard as rpSaveErrorHandler.
  // These should fall through to timeoutHandler / defaultErrorHandler so the user
  // sees the correct timeout or network error modal, not domain-specific copy.
  if (err instanceof NetworkError || err instanceof TypeError) {
    return SKIP
  }

  const detail =
    err.code || err.description
      ? `${err.code || 'Error'}: ${
          err.description || err.message || 'Unknown error'
        }`
      : err.message || 'Unknown error'
  yield put(displayErrorModal(strings.supporting_data_error, [detail]))

  return HANDLED
}
